import { useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';
import type { MouseEvent } from 'react';

import { defaultLocale } from '#features/i18n/constants/locales';
import { i18n } from '#features/i18n/i18n';
import { buildLocaleSwitchPath } from '#features/i18n/utils';
import { envConfig } from '#constants/envConfig';
import { appLinkPath } from '../utils/appLinkPath';
import { isAppLinkable } from '../utils/appLinkable';

/**
 * Открывает ссылку Teamplay внутри приложения. Вернёт false, если ссылка не наша: тогда её
 * должен открыть вызывающий, как открывал раньше.
 *
 * Язык ссылки не важен – важен язык приложения, выбранный человеком. Ссылка без `/ru`
 * у русскоязычного вела на английский адрес, маршрут `/{-$locale}` возвращал на русский,
 * и на холодном старте по ссылке это было бесконечной перезагрузкой (14.09.2026).
 */
export const useOpenAppLink = () => {
  const router = useRouter();

  return useCallback(
    (url: string | null | undefined): boolean => {
      const linked = appLinkPath(url);
      if (!linked) {
        return false;
      }
      // Язык меняем только у пути: функция схлопывает пустые сегменты по всей строке, и
      // `?next=https://x` в запросе превращался в `?next=https:/x`.
      const queryAt = linked.search(/[?#]/);
      const pathname = queryAt === -1 ? linked : linked.slice(0, queryAt);
      const tail = queryAt === -1 ? '' : linked.slice(queryAt);
      const path = `${buildLocaleSwitchPath(pathname, i18n.locale || defaultLocale)}${tail}`;
      if (path !== router.state.location.href) {
        router.history.push(path);
      }
      return true;
    },
    [router]
  );
};

/**
 * Обработчик клика для `<a href target="_blank">`. В оболочке такая ссылка на наш же домен
 * уходит в систему: iPhone открывает Safari без сессии (свою universal link система не
 * подхватывает), Android – Chrome. Внутри приложения её нужно провести через роутер.
 * В вебе и для чужих адресов ничего не делает: ссылка открывается как обычно.
 */
export const useOwnLinkClick = (href: string | null | undefined) => {
  const openAppLink = useOpenAppLink();

  return useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!envConfig.IS_NATIVE || !href) {
        return;
      }
      const linked = appLinkPath(href);
      if (!linked || !isAppLinkable(linked)) {
        return;
      }
      if (openAppLink(href)) {
        event.preventDefault();
      }
    },
    [href, openAppLink]
  );
};
